import { ChevronDownIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useVehicleRecalls from "../../data/useVehicleRecalls";
import { IVehicle } from "../../models/Vehicle";
import formatDateTime from "../../utils/formatDateTime";

export default function VehicleRecalls({ vehicle }: { vehicle: IVehicle }) {
  const { t } = useTranslation("vehicles");
  const { vehicleRecalls } = useVehicleRecalls(vehicle);
  const [isOpen, setIsOpen] = useState(false);

  if (!vehicleRecalls.data?.data || vehicleRecalls.data.data.length === 0) {
    return null;
  }

  const recalls = vehicleRecalls.data.data;

  return (
    <div className="border-t border-gray-200 py-4">
      <div
        className="flex items-center justify-between cursor-pointer select-none mb-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
          <h3 className="text-sm font-medium text-gray-900">
            {t("Vehicle Recalls ({{count}})", { count: recalls.length })}
          </h3>
        </div>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-400 transition-transform duration-200 ease-in-out ${
            isOpen ? "" : "rotate-180"
          }`}
        />
      </div>

      {isOpen && (
        <div className="space-y-4">
          {recalls.map((recall) => (
            <div
              key={recall.id}
              className="bg-amber-50 border border-amber-200 rounded-lg p-4"
            >
              <div className="space-y-2">
                {recall.recallsNumber && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Recall Number:")}
                    </span>
                    <span className="text-xs text-amber-900">{recall.recallsNumber}</span>
                  </div>
                )}
                {recall.launchDate && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Launch Date:")}
                    </span>
                    <span className="text-xs text-amber-900">
                      {formatDateTime(recall.launchDate, DateTime.DATE_SHORT)}
                    </span>
                  </div>
                )}
                {recall.concern && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Concern:")}
                    </span>
                    <span className="text-xs text-amber-900">{recall.concern}</span>
                  </div>
                )}
                {recall.defect && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Defect:")}
                    </span>
                    <span className="text-xs text-amber-900">{recall.defect}</span>
                  </div>
                )}
                {recall.remedy && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Remedy:")}
                    </span>
                    <span className="text-xs text-amber-900">{recall.remedy}</span>
                  </div>
                )}
                {recall.vehicleNumbers && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Vehicles Affected:")}
                    </span>
                    <span className="text-xs text-amber-900">
                      {recall.vehicleNumbers.toLocaleString()}
                    </span>
                  </div>
                )}
                {recall.manufacturerRef && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-amber-800 min-w-[120px]">
                      {t("Manufacturer Ref:")}
                    </span>
                    <span className="text-xs text-amber-900">{recall.manufacturerRef}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
